import cn from "classnames";
import { formatNumber } from "helpers/system";
import React, { useMemo, useState } from "react";
import {
    PerformancePlanExecutionDayModel,
    PerformancePlanExecutionModel,
    PerformancePlanExecutionSubjectModel,
    PerformancePlanMetric,
} from "./types";

type PerformancePlanExecutionProps = {
    execution: PerformancePlanExecutionModel;
};

type ExecutionRow = {
    id: string;
    name: string;
    days: PerformancePlanExecutionDayModel[];
    group?: boolean;
    emphasized?: boolean;
};

const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
        return "—";
    }
    return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
};

const subjectRows = (prefix: string, subjects: PerformancePlanExecutionSubjectModel[]): ExecutionRow[] =>
    subjects.map((subject) => ({ id: `${prefix}-${subject.subjectId}`, name: subject.name, days: subject.days }));

export default function PerformancePlanExecution({ execution }: PerformancePlanExecutionProps) {
    const [metric, setMetric] = useState<PerformancePlanMetric>("mileagePlanPercent");
    const dayCount = execution.organizationDays.length;
    const rows = useMemo<ExecutionRow[]>(
        () => [
            { id: "organization", name: "Компания", days: execution.organizationDays, emphasized: true },
            { id: "logists-group", name: "Логисты", days: [], group: true },
            ...subjectRows("logist", execution.logists),
            { id: "cars-group", name: "Транспортные средства", days: [], group: true },
            ...subjectRows("car", execution.cars),
        ],
        [execution],
    );

    if (!dayCount) {
        return <div className="card card-body text-center text-muted">Нет данных о выполнении за выбранный месяц.</div>;
    }

    return (
        <div className="card overflow-hidden">
            <div className="d-flex flex-wrap align-items-center justify-content-between border-bottom p-3">
                <div className="font-weight-semibold mb-2 mb-sm-0">Процент выполнения по дням</div>
                <div className="btn-group btn-group-sm" role="group" aria-label="Показатель выполнения плана">
                    <button
                        type="button"
                        className={cn("btn", metric === "mileagePlanPercent" ? "btn-brand" : "btn-secondary")}
                        aria-pressed={metric === "mileagePlanPercent"}
                        onClick={() => setMetric("mileagePlanPercent")}
                    >
                        Пробег
                    </button>
                    <button
                        type="button"
                        className={cn("btn", metric === "overallBidsPricePlanPercent" ? "btn-brand" : "btn-secondary")}
                        aria-pressed={metric === "overallBidsPricePlanPercent"}
                        onClick={() => setMetric("overallBidsPricePlanPercent")}
                    >
                        Валовая выручка
                    </button>
                </div>
            </div>
            <div className="table-responsive performance-plans__execution-wrap">
                <table className="table table-sm mb-0 performance-plans__execution-table">
                    <thead>
                        <tr>
                            <th className="performance-plans__execution-object">День</th>
                            {Array.from({ length: dayCount }, (_, index) => (
                                <th key={index} className="text-center">
                                    {String(index + 1).padStart(2, "0")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) =>
                            row.group ? (
                                <tr key={row.id} className="performance-plans__execution-group">
                                    <th className="performance-plans__execution-object">{row.name}</th>
                                    <th colSpan={dayCount} />
                                </tr>
                            ) : (
                                <tr key={row.id} className={cn({ "font-weight-semibold": row.emphasized })}>
                                    <th
                                        className={cn("performance-plans__execution-object", {
                                            "font-weight-normal": !row.emphasized,
                                        })}
                                    >
                                        {row.name}
                                    </th>
                                    {Array.from({ length: dayCount }, (_, index) => (
                                        <td key={index} className="text-center">
                                            {formatPercent(row.days[index]?.[metric])}
                                        </td>
                                    ))}
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
